import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, PhoneIcon, ShieldCheckIcon } from 'lucide-react';
import { FloatingShapes } from './shared/FloatingShapes';
const LOGO_URL = 'https://cdn.magicpatterns.com/patterns/generated-images/eea3d371-fb1c-4cbf-9f61-64776c72582e.jpg';
const HERO_URL = 'https://cdn.magicpatterns.com/patterns/generated-images/26867317-615b-4aee-bbe1-f8601b45d196.jpg';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay: i * 0.12,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
function scrollTo(href: string) {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({
    behavior: 'smooth'
  });
}
export function Hero() {
  return <section id="pocetna" className="relative w-full overflow-hidden bg-cream pt-28 pb-16 sm:pt-32 lg:pt-40 lg:pb-24" data-id="element-183">
      <FloatingShapes data-id="element-184" />
      {/* soft cream wash */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/60 via-cream to-white" data-id="element-185" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-10" data-id="element-186">
        {/* Left */}
        <div data-id="element-187">
          {/* Large logo — a primary visual element */}
          <motion.div variants={fadeUp} custom={0} initial="hidden" animate="show" className="mb-8 flex items-center gap-5" data-id="element-188">
            <span className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-luxury ring-1 ring-gold/30 sm:h-36 sm:w-36 lg:h-44 lg:w-44" data-id="element-189">
              <img src={LOGO_URL} alt="Golden Care logo" className="h-full w-full scale-110 object-cover" data-id="element-190" />
            </span>
            <span className="flex flex-col leading-none" data-id="element-191">
              <span className="font-display text-4xl font-black tracking-tight text-ink sm:text-5xl lg:text-6xl" data-id="element-192">
                GOLDEN
              </span>
              <span className="font-display text-4xl font-black tracking-tight text-ink sm:text-5xl lg:text-6xl" data-id="element-193">
                CARE
              </span>
              <span className="mt-2 text-xs font-semibold uppercase tracking-[0.4em] text-gold-dark sm:text-sm" data-id="element-194">
                Premium Agencija
              </span>
            </span>
          </motion.div>

          <motion.div variants={fadeUp} custom={1} initial="hidden" animate="show" className="mb-5 flex items-center gap-3" data-id="element-195">
            <span className="h-px w-8 bg-gold" data-id="element-196" />
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-gold-dark" data-id="element-197">
              Agencija za kućnu njegu u Tuzli
            </span>
          </motion.div>

          <motion.h1 variants={fadeUp} custom={2} initial="hidden" animate="show" className="font-display text-4xl font-bold leading-[1.05] text-ink text-balance sm:text-5xl lg:text-6xl" data-id="element-198">
            Povjerenje koje traje.
            <span className="mt-2 block italic text-gold" data-id="element-199">
              Briga koja pravi razliku.
            </span>
          </motion.h1>

          <motion.p variants={fadeUp} custom={3} initial="hidden" animate="show" className="mt-6 max-w-xl text-base leading-relaxed text-muted sm:text-lg" data-id="element-200">
            Golden Care povezuje porodice sa profesionalnim njegovateljima i
            njegovateljicama pružajući sigurnu, kvalitetnu i pouzdanu njegu
            starijih i nemoćnih osoba.
          </motion.p>

          <motion.div variants={fadeUp} custom={4} initial="hidden" animate="show" className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center" data-id="element-201">
            <button type="button" onClick={() => scrollTo('#kontakt')} className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold-gradient px-8 py-4 text-sm font-semibold text-white shadow-gold transition-transform hover:scale-[1.03] active:scale-95" data-id="element-202">
              Kontaktirajte nas
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-203" />
            </button>
            <button type="button" onClick={() => scrollTo('#usluge')} className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/50 bg-white/70 px-8 py-4 text-sm font-semibold text-ink backdrop-blur transition-colors hover:border-gold hover:bg-white" data-id="element-204">
              Naše usluge
            </button>
          </motion.div>
        </div>

        {/* Right — hero image with floating card */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.94,
        y: 24
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative" data-id="element-205">
          <div className="relative overflow-hidden rounded-[2rem] shadow-luxury ring-1 ring-black/5" data-id="element-206">
            <img src={HERO_URL} alt="Njegovateljica se brine o starijoj osobi u toplom domu" className="h-[420px] w-full object-cover sm:h-[520px] lg:h-[600px]" loading="eager" data-id="element-207" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent" data-id="element-208" />
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.6
        }} className="glass absolute bottom-5 left-5 right-5 flex items-center gap-4 rounded-2xl p-4 shadow-glass sm:left-6 sm:right-auto sm:max-w-xs" data-id="element-209">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold-gradient text-white shadow-gold" data-id="element-210">
              <PhoneIcon className="h-5 w-5" data-id="element-211" />
            </span>
            <span data-id="element-212">
              <span className="block font-display text-lg font-bold text-ink" data-id="element-213">
                Dostupni 24/7
              </span>
              <span className="text-sm text-muted" data-id="element-214">
                Tuzla, Bosna i Hercegovina
              </span>
            </span>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.8,
          duration: 0.5
        }} className="absolute -top-4 -left-4 hidden items-center gap-2 rounded-full bg-white px-4 py-3 shadow-luxury ring-1 ring-gold/20 sm:flex" data-id="element-215">
            <ShieldCheckIcon className="h-5 w-5 text-gold" data-id="element-216" />
            <span className="text-sm font-semibold text-ink" data-id="element-217">
              Provjereni kandidati
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}